// maths-portll — mounts the DNF offline maths engine (window.MathsGame) for the ACTIVE child and
// wires completed-visit outcomes to Firestore mastery. Full-viewport (position:fixed), NOT .container.
import { useEffect, useRef } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext.jsx";
import { useProfiles } from "../contexts/ProfileContext.jsx";
import { setMastery, getMastery } from "../lib/mastery.js";
import { resolve as resolveTheme } from "../lib/theme.js";
import { getVoice } from "../lib/voice.js";

const SCRIPT_ID = "maths-game-script";

// Idempotently inject /game.js once (StrictMode-safe); resolve when window.MathsGame is ready.
function ensureGameScript() {
  return new Promise((resolve, reject) => {
    if (typeof window !== "undefined" && window.MathsGame) return resolve();
    let s = document.getElementById(SCRIPT_ID);
    if (s) {
      if (window.MathsGame) return resolve();
      s.addEventListener("load", () => resolve(), { once: true });
      s.addEventListener("error", reject, { once: true });
      return;
    }
    s = document.createElement("script");
    s.id = SCRIPT_ID;
    s.src = "/game.js";
    s.async = true;
    s.addEventListener("load", () => resolve(), { once: true });
    s.addEventListener("error", reject, { once: true });
    document.body.appendChild(s);
  });
}

export default function MathsGame() {
  const ref = useRef(null);
  const liveRef = useRef(null);
  const { skillId: deepSkillId } = useParams();   // /app/:skillId → boot straight into this game
  const { user } = useAuth();
  const { activeId, activeChild, displayNameFor } = useProfiles();

  const uid = user?.uid;
  const yearLevel = activeChild?.yearLevel ?? 0;
  const displayName = activeChild ? displayNameFor(activeChild) : "";

  useEffect(() => {
    if (!uid || !activeId || !ref.current) return;
    let cancelled = false;
    const host = ref.current;
    ensureGameScript()
      .then(() => getMastery(uid, activeId).catch(() => ({})))
      .then((seedMastery) => {
        if (cancelled || !window.MathsGame) return;
        // sync the canvas engine to the resolved scheme BEFORE mount so the first frame
        // paints in the right polarity (no dark flash in a light session).
        try {
          window.DNF && window.DNF.theme && window.DNF.theme.setScheme(resolveTheme());
        } catch (e) {}
        window.MathsGame.mount(host, {
          uid,
          childId: activeId,
          yearLevel,
          displayName,
          skill: deepSkillId,
          voice: getVoice(),
          seedMastery,
          announce: (t) => { if (liveRef.current) liveRef.current.textContent = t; },
          writeMastery: (skillId, rec) => setMastery(uid, activeId, skillId, rec),
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      try { window.MathsGame && window.MathsGame.destroy(); } catch (e) {}
    };
  }, [uid, activeId, yearLevel, displayName, deepSkillId]);

  if (!activeId) {
    return (
      <main className="container">
        <h1>Pick a child to play</h1>
        <p>Choose a child profile first, then come back to play.</p>
        <p className="no-print"><Link to="/grown-ups">Grown-Ups' Corner</Link></p>
      </main>
    );
  }

  return (
    <>
      <div ref={ref} style={{ position: "fixed", top: "7.5%", left: "7.5%", width: "85%", height: "85%" }} />
      <div ref={liveRef} aria-live="polite" role="status" style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap" }} />
    </>
  );
}
